import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSerializer } from '@angular/router';
import { authGuard, bdGuard, happyCloseGuard } from '@lentau/auth/application';
import { canMatchB2bMobileCheckout } from '@lentau/checkout/application/matchers/b2b-mobile-checkout-matcher';
import { canMatchPwaB2cCheckout } from '@lentau/checkout/application/matchers/can-match-pwa-b2c-checkout.matcher';
import { OLD_ROUTES } from '@lentau/core/route';
import { SlashUrlSerializer } from '@lentau/core/route/shared/url-serializers/slash-url-serializer';
import {
  DEFAULT_PRELOAD_DELAY_MS,
  DelayedPreloadStrategy,
  ORDERING_PRELOAD_DELAY_MS,
} from '@lentau/core/route/strategies/delayed-preload.strategy';
import { StubRoutes } from '@lentau/core/route/stub.routes';
import { DELIVERY_CODEX_URL_LO } from '@lentau/landings/shared/external-landings-constants';
import { SearchRouteMatcher } from '@lentau/search/shared/search-route-matcher';
import { RouteUrls } from '@lentau/ui-kit/core/enums/route.enum';
import { environmentBrand } from '@lentau/ui-kit/core/environments/environment.brand';
import { BrandsAssets } from '@lentau/ui-kit/core/tokens/constants';
import { PROMO_DETAIL_ROUTE } from '@lentau/promo/promo-detail-lnt';

export const routes: Routes = [
  ...OLD_ROUTES,
  {
    path: RouteUrls.B2bGiftCards,
    loadComponent: () =>
      import('@lentau/landings/pro-card/pages').then((component) => component.ProLandingCardComponent),
  },
  {
    path: RouteUrls.B2BGiftCardsOrder,
    loadComponent: () =>
      import('@lentau/gift-cards/application-cards/b2b/page/b2b-gift-cards-page.component').then(
        (component) => component.GiftCardsPageComponent
      ),
  },
  {
    path: RouteUrls.B2BPlasticGiftCardsOrder,
    loadComponent: () =>
      import(
        '@lentau/gift-cards/application-cards/b2b/page/plastic-gift-card/b2b-plastic-gift-cards-page.component'
      ).then((component) => component.PlasticGiftCardsPageComponent),
  },
  {
    path: RouteUrls.B2BGiftCardDetails,
    loadComponent: () =>
      import(
        '@lentau/gift-cards/application-cards/b2b/page/gift-card-details/b2b-gift-card-details-page.component'
      ).then((component) => component.B2BGiftCardDetailsPageComponent),
  },
  {
    path: RouteUrls.B2BGiftCardBalance,
    loadComponent: () =>
      import('@lentau/gift-cards/application-cards/b2b/page/check-balance/b2b-check-balance-page.component').then(
        (component) => component.CheckBalancePageComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: RouteUrls.B2b,
    loadComponent: () => import('@lentau/landings/pro/pages').then((component) => component.LandingPageComponent),
  },
  {
    path: RouteUrls.Pro,
    loadComponent: () => import('@lentau/landings/pro/pages').then((component) => component.LandingIframeComponent),
  },
  {
    path: RouteUrls.EmailConfirm,
    loadComponent: () =>
      import('@lentau/landings/confirm-email/pages').then((component) => component.LandingsConfirmEmailPagesComponent),
  },
  {
    path: RouteUrls.Ppk,
    loadChildren: () =>
      import('@lentau/loyalty/loyalty-tablets/page/loyalty-tablets.routing').then((m) => m.LOYALTY_TABLETS_ROUTES),
  },
  {
    path: RouteUrls.Ordering,
    canMatch: [canMatchB2bMobileCheckout],
    loadComponent: () =>
      import('@lentau/checkout/pages/checkout-layout-manager/checkout-layout-manager.component').then(
        (m) => m.CheckoutLayoutManagerComponent
      ),
    data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
    children: [
      {
        path: '',
        loadChildren: () => import('@lentau/order/shell/ordering-page.routing').then((mod) => mod.ORDERING_ROUTES),
        data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
      },
    ],
  },
  {
    path: RouteUrls.Ordering,
    canMatch: [canMatchPwaB2cCheckout],
    loadComponent: () =>
      import('@lentau/checkout/pages/checkout-layout-manager/checkout-layout-manager.component').then(
        (m) => m.CheckoutLayoutManagerComponent
      ),
    data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
    children: [
      {
        path: '',
        loadChildren: () => import('@lentau/order/shell/ordering-page.routing').then((mod) => mod.ORDERING_ROUTES),
        data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
      },
    ],
  },
  {
    path: RouteUrls.Ordering,
    loadComponent: () =>
      import('@lentau/layout/template/features/components/layout-ordering/layout-ordering.component').then(
        (mod) => mod.LayoutOrderingComponent
      ),
    data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
    children: [
      {
        path: '',
        loadChildren: () => import('@lentau/order/shell/ordering-page.routing').then((mod) => mod.ORDERING_ROUTES),
        data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
      },
    ],
  },
  {
    path: RouteUrls.Sg1,
    loadComponent: () =>
      import('@lentau/landings/severgroup/pages/severgroup-pages/sg-01/sg-01.component').then(
        (m) => m.SevergroupSg01Component
      ),
  },
  {
    path: RouteUrls.Sg4,
    loadComponent: () =>
      import('@lentau/landings/severgroup/pages/severgroup-pages/sg-01/sg-01.component').then(
        (m) => m.SevergroupSg01Component
      ),
  },
  {
    path: RouteUrls.ReferralLandings,
    loadChildren: () =>
      import('@lentau/referral-landings/referral-landing.routing').then((m) => m.REFERRAL_LANDINGS_ROUTES),
  },
  {
    path: '',
    loadComponent: () =>
      import('@lentau/layout/template/features/components/layout/layout.component').then((mod) => mod.LayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@lentau/main/pages/main-page/main-page.component').then((mod) => mod.MainPageComponent),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: RouteUrls.Basket,
        loadChildren: () => import('@lentau/cart/page/cart-page.module').then((m) => m.CartPageModule),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: RouteUrls.Faq,
        loadChildren: () => import('@lentau/faq/shell/faq.routing').then((m) => m.FAQ_ROUTES),
      },
      {
        path: RouteUrls.Info,
        loadChildren: () => import('@lentau/info/pages/info.routing').then((m) => m.INFO_ROUTES),
      },
      {
        path: 'article',
        loadChildren: () => import('@lentau/article/page/routes').then((m) => m.articlePageRoutes),
      },
      {
        path: 'contacts',
        loadChildren: () => import('@lentau/contacts/page/routes').then((m) => m.contactsPageRoutes),
      },
      {
        path: RouteUrls.Authentication,
        loadComponent: () => import('@lentau/auth/page'),
      },
      {
        path: RouteUrls.AuthSocialCallback,
        loadComponent: () =>
          import('@lentau/auth/features/components/social-auth-callback/social-auth-callback.component').then(
            (m) => m.SocialAuthCallbackComponent
          ),
      },
      {
        path: RouteUrls.Login,
        loadChildren: () =>
          import('@lentau/registration/b2b/page/b2b-registration.routing').then((m) => m.B2B_REGISTRATION_ROUTES),
      },
      { path: RouteUrls.CatalogOld, redirectTo: RouteUrls.Catalog },
      { path: RouteUrls.MainOld, redirectTo: '' },
      { path: RouteUrls.BasketOld, redirectTo: RouteUrls.Basket },
      { path: RouteUrls.ProfileOld, redirectTo: RouteUrls.MyAccount },
      { path: RouteUrls.OrderFeedbackOld, redirectTo: RouteUrls.OrderFeedback },
      { path: RouteUrls.OrderFeedbackOld2, redirectTo: RouteUrls.OrderFeedback },
      {
        path: RouteUrls.Catalog,
        loadChildren: () => import('@lentau/catalog/page/catalog-page.routes').then((m) => m.CATALOG_PAGE_ROUTES),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        matcher: SearchRouteMatcher,
        loadChildren: () =>
          import('@lentau/search-redesign/listing/page/lib/search-page/search-page.routing.module').then(
            (m) => m.SearchPageRoutingModule
          ),
      },
      {
        path: `${RouteUrls.Item}/:itemId`,
        loadComponent: () =>
          import('@lentau/product-redesign/page/product-page/product-page.component').then(
            (m) => m.ProductPageComponent
          ),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: `${RouteUrls.Product}/:slugName`,
        loadComponent: () =>
          import('@lentau/product-redesign/page/product-page/product-page.component').then(
            (m) => m.ProductPageComponent
          ),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: `${RouteUrls.Brand}/:slug`,
        loadChildren: () => import('@lentau/brands/feature').then((m) => m.BRANDS_ROUTES),
      },
      {
        path: `${RouteUrls.MassCoupon}/:couponUrl`,
        loadComponent: () =>
          import('@lentau/loyalty/mass-coupon/page/mass-coupon/mass-coupon.component').then(
            (m) => m.MassCouponComponent
          ),
      },
      {
        path: RouteUrls.RecipesArticles,
        loadChildren: () =>
          import('@lentau/recipes/articles-list/page/routes').then((m) => m.recipesArticlesListRoutes),
      },
      {
        path: RouteUrls.MyAccount,
        loadChildren: () => import('@lentau/profile/shell/profile-page-routing.module'),
      },
      {
        path: RouteUrls.MyAccountB2B,
        loadChildren: () => import('@lentau/profile/shell/profile-page-b2b-routing.module'),
      },
      {
        path: RouteUrls.MyAccountB2BMobile,
        loadChildren: () => import('@lentau/profile/shell/profile-page-b2b-mobile-routing.module'),
      },
      {
        path: RouteUrls.B2CGiftCardsOrder,
        loadComponent: () =>
          import('@lentau/gift-cards/application-cards/b2c/page/b2c-gift-cards-page.component').then(
            (component) => component.B2CGiftCardsPageComponent
          ),
      },
      PROMO_DETAIL_ROUTE,
      {
        path: RouteUrls.Action,
        loadComponent: () => import('@lentau/promo/promo-list-lnt/features').then((m) => m.ActionListComponent),
      },
      {
        path: RouteUrls.Landing,
        loadChildren: () => import('@lentau/landings/pages/landing.routing').then((m) => m.LANDING_ROUTES),
      },
      {
        path: RouteUrls.DynamicLandings,
        loadChildren: () => import('@lentau/landings/pages/dynamic-landing.routing').then((m) => m.LANDINGS_ROUTES),
      },
      {
        path: RouteUrls.HappyClub,
        canActivate: [happyCloseGuard],
        loadChildren: () =>
          import('@lentau/loyalty/happyclub/page/happy-club.routing').then((m) => m.HAPPY_CLUB_ROUTES),
      },
      {
        path: RouteUrls.DiscoveryClub,
        loadChildren: () =>
          import('@lentau/loyalty/discoveryclub/page/discovery-club.routing').then((m) => m.DISCOVERY_CLUB_ROUTES),
      },
      {
        path: RouteUrls.BarClub,
        loadChildren: () => import('@lentau/loyalty/barclub/page/bar-club.routing').then((m) => m.BAR_CLUB_ROUTES),
      },
      {
        path: RouteUrls.ZooClub,
        loadChildren: () => import('@lentau/loyalty/zooclub/pages/zoo-club.routing').then((m) => m.ZOO_CLUB_ROUTES),
      },
      {
        path: RouteUrls.DeliveryCodex,
        loadComponent: () =>
          import('@lentau/landings/pages/external-landing-viewer/external-landing-viewer.component').then(
            (m) => m.ExternalLandingViewerComponent
          ),
        canMatch: [() => environmentBrand.RETAIL_BRAND === BrandsAssets.MONETKA],
        data: { url: DELIVERY_CODEX_URL_LO },
      },
      {
        path: RouteUrls.CompaniesSwitcher,
        loadComponent: () =>
          import('@lentau/profile/organizations/companies-switcher/page/companies-switcher-page.component').then(
            (m) => m.CompaniesSwitcherPageComponent
          ),
      },
      {
        path: RouteUrls.BdCoupon,
        canActivate: [bdGuard],
        loadChildren: () => import('@lentau/profile/shell/profile-page-routing.module'),
      },
      {
        path: 'similar-products',
        data: { header: 'Похожие товары' },
        loadChildren: () =>
          import('@lentau/listing/pages/similar-products/similar-products.page').then(
            (m) => m.SimilarProductsPageModule
          ),
      },
      ...StubRoutes.map((path: string) => ({
        path,
        loadComponent: () => import('@lentau/not-found/page/not-found-stub').then((mod) => mod.NotFoundStubComponent),
      })),
      {
        path: RouteUrls.NotFound,
        loadComponent: () =>
          import('@lentau/not-found/page/not-found-page/not-found-page.component').then((m) => m.NotFoundPageComponent),
        data: { status: 404 },
      },
      {
        path: '**',
        loadComponent: () =>
          import('@lentau/not-found/page/not-found-page/not-found-page.component').then((m) => m.NotFoundPageComponent),
        data: { status: 404 },
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledNonBlocking',
      anchorScrolling: 'enabled',
      paramsInheritanceStrategy: 'emptyOnly',
      bindToComponentInputs: true,
      onSameUrlNavigation: 'reload',
      preloadingStrategy: DelayedPreloadStrategy,
    }),
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: UrlSerializer,
      useClass: SlashUrlSerializer,
    },
  ],
})
export class AppRoutingModule {}
